import { Box, Typography, Card, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import { colors } from '@src/styles';
import {
  cardTitleSX,
  fieldIconButtonSX,
  iconTileSX,
  monoFontFamily,
  outlineButtonSX,
  profileCardSX,
  secretFieldSX,
} from '../profileStyles';

export type ProfileSignatureCardProps = {
  signatureKey: string | null;
  isLoadingSignature: boolean;
  isRefreshingSignature: boolean;
  showSignature: boolean;
  onGetSignature: () => void;
  onRefreshSignature: () => void;
  onCopySignature: () => void;
  onToggleSignatureVisibility: () => void;
};

export const ProfileSignatureCard = ({
  signatureKey,
  isLoadingSignature,
  isRefreshingSignature,
  showSignature,
  onGetSignature,
  onRefreshSignature,
  onCopySignature,
  onToggleSignatureVisibility,
}: ProfileSignatureCardProps) => {
  return (
    <Card elevation={0} sx={{ ...profileCardSX, gridColumn: { xs: '1', md: '1 / -1' }, padding: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, marginBottom: 2 }}>
        <Box sx={iconTileSX}>
          <DrawOutlinedIcon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={cardTitleSX}>
            Chave de Assinatura
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: colors.brand.textSecondary }}>
            Recupere a chave atual ou gere uma nova.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 1, alignItems: 'center' }}>
        <Box sx={{ ...secretFieldSX, flexBasis: { xs: '100%', sm: 'auto' } }}>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: monoFontFamily,
              fontSize: '12.5px',
              color: signatureKey ? colors.brand.textStrong : colors.brand.textMuted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {signatureKey
              ? showSignature
                ? signatureKey
                : `${'•'.repeat(signatureKey.length)}`
              : 'Nenhuma chave carregada'}
          </Typography>
          <Tooltip title={showSignature ? 'Esconder' : 'Mostrar'} arrow>
            <IconButton
              onClick={onToggleSignatureVisibility}
              aria-label={showSignature ? 'Esconder chave' : 'Mostrar chave'}
              sx={fieldIconButtonSX}
            >
              {showSignature ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Copiar" arrow>
            <IconButton onClick={onCopySignature} aria-label="Copiar chave" sx={fieldIconButtonSX}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Button
          variant="outlined"
          startIcon={
            isLoadingSignature ? <CircularProgress sx={{ color: colors.primary.yellow }} size={15} /> : <KeyIcon />
          }
          onClick={onGetSignature}
          disabled={isLoadingSignature}
          sx={{ ...outlineButtonSX, flex: { xs: 1, sm: 'none' } }}
        >
          Recuperar
        </Button>
        <Button
          variant="outlined"
          startIcon={
            isRefreshingSignature ? (
              <CircularProgress sx={{ color: colors.primary.yellow }} size={15} />
            ) : (
              <RefreshIcon />
            )
          }
          onClick={onRefreshSignature}
          disabled={isRefreshingSignature}
          sx={{ ...outlineButtonSX, flex: { xs: 1, sm: 'none' } }}
        >
          Gerar
        </Button>
      </Box>
    </Card>
  );
};
